import { useState, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { 
  Table2, Upload, Loader2, Sparkles, Download, FileSpreadsheet,
  Calculator, ImageIcon, BarChart3, FileText, FileType,
  Lightbulb, TrendingUp, Zap, MessageSquare, Send, AlertTriangle,
  PieChart, LineChart
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function PowerExcel() {
  // Data state
  const [uploadedData, setUploadedData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  
  // Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Formula state
  const [formulaDescription, setFormulaDescription] = useState("");
  const [formulaContext, setFormulaContext] = useState("");
  const [isGeneratingFormula, setIsGeneratingFormula] = useState(false);
  const [formulaResult, setFormulaResult] = useState("");
  
  // Image to table state
  const [imageFile, setImageFile] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [convertedData, setConvertedData] = useState(null);
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);
  
  // Natural Language Query state
  const [loadedFileId, setLoadedFileId] = useState(null);
  const [nlQuery, setNlQuery] = useState("");
  const [nlQueryResult, setNlQueryResult] = useState(null);
  const [isQuerying, setIsQuerying] = useState(false);
  const [queryHistory, setQueryHistory] = useState([]);
  
  // Error detection state
  const [errorDetectionResult, setErrorDetectionResult] = useState(null);
  const [isDetectingErrors, setIsDetectingErrors] = useState(false);
  
  // Chart state
  const [chartConfig, setChartConfig] = useState({ type: "bar", xColumn: "", yColumn: "" });
  const [chartResult, setChartResult] = useState(null);
  const [isGeneratingChart, setIsGeneratingChart] = useState(false);
  
  const chatScrollRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    setUploadedData(null);
    setAnalysisResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(`${API}/api/smart-office/excel/upload`, formData, {
        headers: getHeaders(),
        withCredentials: true
      });

      setUploadedData(res.data);
      setColumns(res.data.columns);
      
      // Also load the file for NLQ queries
      const loadRes = await axios.post(`${API}/api/smart-office/excel/load`, formData, {
        headers: getHeaders(),
        withCredentials: true
      });
      
      if (loadRes.data.file_id) {
        setLoadedFileId(loadRes.data.file_id);
      }
      
      toast.success(`Loaded ${res.data.row_count} rows from ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load file");
    }
    setIsUploading(false);
  };

  const handleAnalyze = async () => {
    if (!uploadedData?.full_data) {
      toast.error("Please upload data first");
      return;
    }

    setIsAnalyzing(true);
    setAnalysisResult(null);

    try {
      const res = await axios.post(`${API}/api/smart-office/excel/analyze`, {
        data: uploadedData.full_data
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });

      setAnalysisResult(res.data);
      toast.success("Analysis complete");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    }
    setIsAnalyzing(false);
  };

  const handleGenerateFormula = async () => {
    if (!formulaDescription.trim()) {
      toast.error("Please describe what you need");
      return;
    }

    setIsGeneratingFormula(true);
    setFormulaResult("");

    try {
      const res = await axios.post(`${API}/api/smart-office/excel/formula`, {
        description: formulaDescription,
        context: formulaContext
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });

      setFormulaResult(res.data.formula_response);
      toast.success("Formula generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate formula");
    }
    setIsGeneratingFormula(false);
  };

  const handleImageToTable = async () => {
    if (!imageFile) {
      toast.error("Please select an image");
      return;
    }

    setIsConverting(true);
    setConvertedData(null);

    try {
      const formData = new FormData();
      formData.append("file", imageFile);

      const res = await axios.post(`${API}/api/smart-office/excel/image-to-table`, formData, {
        headers: getHeaders(),
        withCredentials: true
      });

      if (res.data.success) {
        setConvertedData(res.data);
        toast.success(`Extracted ${res.data.row_count} rows from image`);
      } else {
        toast.error("Could not extract table from image");
        setConvertedData({ raw_response: res.data.raw_response });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Conversion failed");
    }
    setIsConverting(false);
  };

  const handleExport = async (format) => {
    const dataToExport = convertedData?.data || uploadedData?.full_data;
    if (!dataToExport) {
      toast.error("No data to export");
      return;
    }

    setIsExporting(true);

    try {
      let endpoint, payload;
      
      if (format === 'excel') {
        endpoint = `${API}/api/smart-office/excel/create-excel`;
        payload = { data: dataToExport, include_charts: true };
      } else if (format === 'pdf') {
        endpoint = `${API}/api/smart-office/excel/create-pdf`;
        payload = { data: dataToExport, title: "Data Export" };
      }

      const res = await axios.post(endpoint, payload, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });

      // Download the file
      window.open(`${API}${res.data.download_url}`, '_blank');
      toast.success(`${format.toUpperCase()} file created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    }
    setIsExporting(false);
  };

  const handleNLQuery = async () => {
    if (!nlQuery.trim() || !loadedFileId) {
      toast.error(loadedFileId ? "Please enter a question" : "Please upload a file first");
      return;
    }

    setIsQuerying(true);
    const userQuery = nlQuery;
    setNlQuery("");
    
    // Add user query to history
    setQueryHistory(prev => [...prev, { role: "user", content: userQuery }]);

    try {
      const res = await axios.post(`${API}/api/smart-office/excel/${loadedFileId}/query`, {
        query: userQuery
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });

      setNlQueryResult(res.data);
      setQueryHistory(prev => [...prev, { 
        role: "assistant", 
        content: res.data.response,
        meta: `${res.data.rows_analyzed} rows analyzed`
      }]);
      
      // Scroll to bottom of chat
      setTimeout(() => {
        if (chatScrollRef.current) {
          chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
      }, 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Query failed");
      setQueryHistory(prev => [...prev, { 
        role: "assistant", 
        content: "Sorry, I couldn't process that query. Please try rephrasing.", 
        error: true 
      }]);
    }
    setIsQuerying(false);
  };

  const handleDetectErrors = async () => {
    if (!loadedFileId) {
      toast.error("Please upload a file first");
      return;
    }

    setIsDetectingErrors(true);
    setErrorDetectionResult(null);

    try {
      const res = await axios.post(`${API}/api/smart-office/excel/${loadedFileId}/detect-errors`, {}, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });

      setErrorDetectionResult(res.data);
      toast.success(`Found ${res.data.total_issues} potential issues`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error detection failed");
    }
    setIsDetectingErrors(false);
  };

  const handleGenerateChart = async () => {
    if (!loadedFileId) {
      toast.error("Please upload a file first");
      return;
    }

    setIsGeneratingChart(true);
    setChartResult(null);

    try {
      const res = await axios.post(`${API}/api/smart-office/excel/${loadedFileId}/chart`, {
        chart_type: chartConfig.type,
        x_column: chartConfig.xColumn || null,
        y_column: chartConfig.yColumn || null
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });

      setChartResult(res.data);
      toast.success("Chart suggestions generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chart generation failed");
    }
    setIsGeneratingChart(false);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <Table2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">
              POWER EXCEL
            </h1>
            <p className="text-slate-600">AI-powered spreadsheet assistant & data analysis</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="query" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-none">
          <TabsTrigger value="query" className="rounded-none data-[state=active]:bg-white" data-testid="tab-query">
            <MessageSquare className="w-4 h-4 mr-2" />
            Ask Questions
          </TabsTrigger>
          <TabsTrigger value="analyze" className="rounded-none data-[state=active]:bg-white" data-testid="tab-analyze">
            <TrendingUp className="w-4 h-4 mr-2" />
            Analyze Data
          </TabsTrigger>
          <TabsTrigger value="errors" className="rounded-none data-[state=active]:bg-white" data-testid="tab-errors">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Detect Errors
          </TabsTrigger>
          <TabsTrigger value="charts" className="rounded-none data-[state=active]:bg-white" data-testid="tab-charts">
            <PieChart className="w-4 h-4 mr-2" />
            Charts
          </TabsTrigger>
          <TabsTrigger value="formula" className="rounded-none data-[state=active]:bg-white" data-testid="tab-formula">
            <Calculator className="w-4 h-4 mr-2" />
            Formulas
          </TabsTrigger>
          <TabsTrigger value="export" className="rounded-none data-[state=active]:bg-white" data-testid="tab-export">
            <Download className="w-4 h-4 mr-2" />
            Export
          </TabsTrigger>
        </TabsList>

        {/* Natural Language Query Tab */}
        <TabsContent value="query">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Upload Section */}
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-green-500" />
                  Upload Spreadsheet
                </h3>
                <div className="border-2 border-dashed border-slate-300 p-6 text-center hover:border-green-400 transition-colors">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="excel-nlq-upload"
                    data-testid="excel-nlq-upload"
                  />
                  <label htmlFor="excel-nlq-upload" className="cursor-pointer">
                    {isUploading ? (
                      <Loader2 className="w-10 h-10 text-green-500 mx-auto mb-2 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                    )}
                    <p className="text-slate-600 font-medium">
                      {uploadedData ? uploadedData.filename || "File loaded" : "Drop CSV or Excel"}
                    </p>
                    {uploadedData && (
                      <p className="text-sm text-green-600 mt-1">
                        {uploadedData.row_count} rows • {columns.length} columns
                      </p>
                    )}
                  </label>
                </div>
                
                {loadedFileId && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 text-sm">
                    <p className="text-green-700 font-medium flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Ready for questions!
                    </p>
                    <p className="text-green-600 text-xs mt-1">
                      Columns: {columns.slice(0, 5).join(", ")}{columns.length > 5 ? "..." : ""}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Sample Questions */}
              <div className="bg-white border border-slate-200 p-4">
                <h4 className="text-sm font-medium text-slate-700 mb-3">Try asking:</h4>
                <div className="space-y-2">
                  {[
                    "What is the total sum?",
                    "Find the average by category",
                    "Show top 10 by value",
                    "What trends do you see?",
                    "Any outliers in the data?"
                  ].map((q, i) => (
                    <button
                      key={i}
                      onClick={() => setNlQuery(q)}
                      className="w-full text-left text-sm p-2 bg-app hover:bg-green-50 text-slate-600 hover:text-green-700 transition-colors"
                    >
                      "{q}"
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Chat Interface */}
            <div className="lg:col-span-2 bg-white border border-slate-200 flex flex-col" style={{ height: "70vh" }}>
              <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-green-500 to-emerald-600">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Ask Questions About Your Data
                </h3>
                <p className="text-green-100 text-sm">Natural language queries powered by GPT-5.2</p>
              </div>

              <ScrollArea className="flex-1 p-4" ref={chatScrollRef}>
                {queryHistory.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <Table2 className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                      <p className="text-slate-500 mb-2">Upload a spreadsheet to get started</p>
                      <p className="text-sm text-slate-400">
                        Ask questions like "What's the total revenue?" or<br />
                        "Find customers with orders over $1000"
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {queryHistory.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`p-4 ${
                          msg.role === "user"
                            ? "bg-green-50 border-l-4 border-green-500 ml-8"
                            : msg.error
                            ? "bg-red-50 border-l-4 border-red-500 mr-8"
                            : "bg-app border-l-4 border-slate-300 mr-8"
                        }`}
                      >
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.content}</p>
                        {msg.meta && (
                          <p className="text-xs text-slate-500 mt-2">{msg.meta}</p>
                        )}
                      </div>
                    ))}
                    {isQuerying && (
                      <div className="bg-app border-l-4 border-slate-300 mr-8 p-4">
                        <Loader2 className="w-5 h-5 text-green-500 animate-spin" />
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>

              <div className="p-4 border-t border-slate-200">
                <div className="flex gap-2">
                  <Input
                    value={nlQuery}
                    onChange={(e) => setNlQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleNLQuery()}
                    placeholder={loadedFileId ? "Ask a question about your data..." : "Upload a file first"}
                    disabled={!loadedFileId || isQuerying}
                    className="flex-1 rounded-none"
                    data-testid="nlq-input"
                  />
                  <Button
                    onClick={handleNLQuery}
                    disabled={!loadedFileId || !nlQuery.trim() || isQuerying}
                    className="bg-green-600 hover:bg-green-500 rounded-none"
                    data-testid="nlq-send-btn"
                  >
                    {isQuerying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Analyze Data Tab */}
        <TabsContent value="analyze">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upload & Data Preview */}
            <div className="space-y-6">
              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-green-500" />
                  Upload Spreadsheet
                </h3>
                <div className="border-2 border-dashed border-slate-300 p-6 text-center hover:border-green-400 transition-colors">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="excel-upload"
                    data-testid="excel-upload-input"
                  />
                  <label htmlFor="excel-upload" className="cursor-pointer">
                    {isUploading ? (
                      <Loader2 className="w-10 h-10 text-green-500 mx-auto mb-2 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                    )}
                    <p className="text-slate-600 font-medium">Drop CSV or Excel file here</p>
                    <p className="text-sm text-slate-500 mt-1">CSV, XLSX, XLS</p>
                  </label>
                </div>
              </div>

              {uploadedData && (
                <div className="bg-white border border-slate-200 p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-slate-800">Data Preview</h3>
                    <span className="text-sm text-slate-500">{uploadedData.row_count} rows</span>
                  </div>
                  <ScrollArea className="h-[300px] border border-slate-200">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          {columns.map((col, i) => (
                            <th key={i} className="p-2 text-left font-medium text-slate-700 border-b">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {uploadedData.data.slice(0, 20).map((row, i) => (
                          <tr key={i} className="border-b hover:bg-app">
                            {columns.map((col, j) => (
                              <td key={j} className="p-2 text-slate-600">{row[col]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                  
                  <Button
                    onClick={handleAnalyze}
                    disabled={isAnalyzing}
                    className="w-full mt-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 rounded-none"
                    data-testid="analyze-btn"
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Analyze with AI
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Analysis Results */}
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-500" />
                AI Insights
              </h3>
              
              {analysisResult ? (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-4">
                    {/* Statistics */}
                    <div className="bg-app p-4 rounded">
                      <h4 className="font-medium text-slate-700 mb-2">Data Statistics</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-slate-500">Rows:</span>{" "}
                          <span className="font-medium">{analysisResult.statistics.rows}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Columns:</span>{" "}
                          <span className="font-medium">{analysisResult.statistics.columns}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* AI Insights */}
                    <div>
                      <h4 className="font-medium text-slate-700 mb-2">Analysis</h4>
                      <pre className="text-sm text-slate-600 whitespace-pre-wrap font-['IBM_Plex_Sans'] leading-relaxed">
                        {analysisResult.ai_insights}
                      </pre>
                    </div>
                  </div>
                </ScrollArea>
              ) : (
                <div className="h-[500px] flex items-center justify-center border border-dashed border-slate-300">
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Upload data to see AI insights</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Error Detection Tab */}
        <TabsContent value="errors">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Data Quality Check
              </h3>
              
              <div className="space-y-4">
                <p className="text-slate-600 text-sm">
                  AI-powered analysis to detect potential errors, inconsistencies, and data quality issues in your spreadsheet.
                </p>
                
                {!loadedFileId ? (
                  <div className="p-6 text-center border-2 border-dashed border-slate-300">
                    <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Upload a file in the "Ask Questions" tab first</p>
                  </div>
                ) : (
                  <>
                    <div className="p-4 bg-amber-50 border border-amber-200">
                      <p className="text-amber-700 text-sm font-medium">
                        File loaded with {columns.length} columns
                      </p>
                      <p className="text-amber-600 text-xs mt-1">
                        {uploadedData?.row_count || 0} rows ready for analysis
                      </p>
                    </div>
                    
                    <Button
                      onClick={handleDetectErrors}
                      disabled={isDetectingErrors}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 rounded-none"
                      data-testid="detect-errors-btn"
                    >
                      {isDetectingErrors ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          Detect Potential Errors
                        </>
                      )}
                    </Button>
                  </>
                )}
                
                <div className="text-xs text-slate-500 space-y-1">
                  <p>• Mixed data types in columns</p>
                  <p>• Missing/null values</p>
                  <p>• Statistical outliers</p>
                  <p>• Inconsistent formatting</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Detection Results</h3>
              {errorDetectionResult ? (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-4">
                    <div className="p-3 bg-amber-50 border border-amber-200">
                      <span className="text-2xl font-bold text-amber-600">{errorDetectionResult.total_issues}</span>
                      <span className="text-amber-600 ml-2">potential issues found</span>
                    </div>
                    
                    {errorDetectionResult.detected_issues?.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-medium text-slate-700">Issues by Column:</h4>
                        {errorDetectionResult.detected_issues.map((issue, idx) => (
                          <div key={idx} className={`p-3 border-l-4 ${
                            issue.type === "outliers" ? "border-red-500 bg-red-50" :
                            issue.type === "null_values" ? "border-amber-500 bg-amber-50" :
                            "border-blue-500 bg-blue-50"
                          }`}>
                            <p className="font-medium text-slate-700">{issue.column}</p>
                            <p className="text-sm text-slate-600">{issue.description}</p>
                            <span className="text-xs text-slate-500 uppercase">{issue.type}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {errorDetectionResult.ai_analysis && (
                      <div>
                        <h4 className="font-medium text-slate-700 mb-2">AI Analysis:</h4>
                        <pre className="text-sm text-slate-600 whitespace-pre-wrap bg-app p-4">
                          {errorDetectionResult.ai_analysis}
                        </pre>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              ) : (
                <div className="h-[500px] flex items-center justify-center border border-dashed border-slate-300">
                  <div className="text-center">
                    <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Run analysis to see potential issues</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Charts Tab */}
        <TabsContent value="charts">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-blue-500" />
                Chart Configuration
              </h3>
              
              {!loadedFileId ? (
                <div className="p-6 text-center border-2 border-dashed border-slate-300">
                  <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Upload a file in the "Ask Questions" tab first</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Chart Type</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { type: "bar", icon: BarChart3, label: "Bar" },
                        { type: "line", icon: LineChart, label: "Line" },
                        { type: "pie", icon: PieChart, label: "Pie" }
                      ].map(({ type, icon: Icon, label }) => (
                        <button
                          key={type}
                          onClick={() => setChartConfig(prev => ({ ...prev, type }))}
                          className={`p-3 border flex flex-col items-center gap-1 ${
                            chartConfig.type === type 
                              ? "border-blue-500 bg-blue-50 text-blue-600" 
                              : "border-slate-200 text-slate-600 hover:bg-app"
                          }`}
                        >
                          <Icon className="w-6 h-6" />
                          <span className="text-xs">{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">X-Axis Column (Optional)</label>
                    <select
                      value={chartConfig.xColumn}
                      onChange={(e) => setChartConfig(prev => ({ ...prev, xColumn: e.target.value }))}
                      className="w-full p-2 border border-slate-300 bg-white text-slate-700"
                    >
                      <option value="">Auto-detect</option>
                      {columns.map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Y-Axis Column (Optional)</label>
                    <select
                      value={chartConfig.yColumn}
                      onChange={(e) => setChartConfig(prev => ({ ...prev, yColumn: e.target.value }))}
                      className="w-full p-2 border border-slate-300 bg-white text-slate-700"
                    >
                      <option value="">Auto-detect (numeric)</option>
                      {columns.map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                  
                  <Button
                    onClick={handleGenerateChart}
                    disabled={isGeneratingChart}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 rounded-none"
                    data-testid="generate-chart-btn"
                  >
                    {isGeneratingChart ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Chart Suggestions
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>

            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Chart Suggestions</h3>
              {chartResult ? (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 border border-blue-200">
                      <p className="font-medium text-blue-700">Chart Type: {chartResult.chart_type}</p>
                      {chartResult.x_column && <p className="text-sm text-blue-600">X: {chartResult.x_column}</p>}
                      {chartResult.y_column && <p className="text-sm text-blue-600">Y: {chartResult.y_column}</p>}
                    </div>
                    
                    {chartResult.data_points?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-slate-700 mb-2">Data Points ({chartResult.data_points.length}):</h4>
                        <ScrollArea className="h-40 border border-slate-200">
                          <table className="w-full text-sm">
                            <tbody>
                              {chartResult.data_points.slice(0, 20).map((point, idx) => (
                                <tr key={idx} className="border-b">
                                  {Object.entries(point).map(([k, v], i) => (
                                    <td key={i} className="p-2 text-slate-600">{String(v)}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </ScrollArea>
                      </div>
                    )}
                    
                    {chartResult.ai_suggestions && (
                      <div>
                        <h4 className="font-medium text-slate-700 mb-2">AI Recommendations:</h4>
                        <pre className="text-sm text-slate-600 whitespace-pre-wrap bg-app p-4">
                          {chartResult.ai_suggestions}
                        </pre>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              ) : (
                <div className="h-[500px] flex items-center justify-center border border-dashed border-slate-300">
                  <div className="text-center">
                    <PieChart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Configure and generate chart suggestions</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Formula Generator Tab */}
        <TabsContent value="formula">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-purple-500" />
                Describe Your Formula
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">What do you need?</label>
                  <Textarea
                    placeholder="E.g., Calculate the sum of column A where column B equals 'Yes', or find the average of sales grouped by region..."
                    value={formulaDescription}
                    onChange={(e) => setFormulaDescription(e.target.value)}
                    className="min-h-[120px] bg-app border-slate-300 rounded-none"
                    data-testid="formula-description"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Context (Optional)</label>
                  <Textarea
                    placeholder="Describe your data structure, column names, or any specific requirements..."
                    value={formulaContext}
                    onChange={(e) => setFormulaContext(e.target.value)}
                    className="min-h-[80px] bg-app border-slate-300 rounded-none"
                    data-testid="formula-context"
                  />
                </div>
                
                <Button
                  onClick={handleGenerateFormula}
                  disabled={isGeneratingFormula || !formulaDescription.trim()}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 rounded-none"
                  data-testid="generate-formula-btn"
                >
                  {isGeneratingFormula ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating Formula...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Generate Formula
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Formula Result</h3>
              {formulaResult ? (
                <ScrollArea className="h-[400px] border border-slate-200 p-4 bg-app">
                  <pre className="text-sm text-slate-700 whitespace-pre-wrap font-['JetBrains_Mono']">
                    {formulaResult}
                  </pre>
                </ScrollArea>
              ) : (
                <div className="h-[400px] flex items-center justify-center border border-dashed border-slate-300">
                  <div className="text-center">
                    <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Formula will appear here</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Image to Table Tab */}
        <TabsContent value="image">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-orange-500" />
                Upload Table Image
              </h3>
              
              <div className="border-2 border-dashed border-slate-300 p-8 text-center hover:border-orange-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0])}
                  className="hidden"
                  id="image-upload"
                  data-testid="image-upload-input"
                />
                <label htmlFor="image-upload" className="cursor-pointer">
                  <ImageIcon className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  {imageFile ? (
                    <p className="text-orange-600 font-medium">{imageFile.name}</p>
                  ) : (
                    <>
                      <p className="text-slate-600 font-medium">Drop image of table here</p>
                      <p className="text-sm text-slate-500 mt-1">PNG, JPG, WEBP</p>
                    </>
                  )}
                </label>
              </div>
              
              <Button
                onClick={handleImageToTable}
                disabled={isConverting || !imageFile}
                className="w-full mt-4 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 rounded-none"
                data-testid="convert-image-btn"
              >
                {isConverting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Extracting Data...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Convert to Table
                  </>
                )}
              </Button>
              
              <p className="text-xs text-slate-500 mt-3 text-center">
                Uses GPT-5.2 Vision to extract table data from images
              </p>
            </div>

            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Extracted Data</h3>
              {convertedData?.data ? (
                <div className="space-y-4">
                  <ScrollArea className="h-[300px] border border-slate-200">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          {Object.keys(convertedData.data[0] || {}).map((col, i) => (
                            <th key={i} className="p-2 text-left font-medium text-slate-700 border-b">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {convertedData.data.map((row, i) => (
                          <tr key={i} className="border-b hover:bg-app">
                            {Object.values(row).map((val, j) => (
                              <td key={j} className="p-2 text-slate-600">{String(val)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleExport('excel')}
                      disabled={isExporting}
                      variant="outline"
                      className="flex-1 rounded-none"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Export Excel
                    </Button>
                    <Button
                      onClick={() => handleExport('pdf')}
                      disabled={isExporting}
                      variant="outline"
                      className="flex-1 rounded-none"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Export PDF
                    </Button>
                  </div>
                </div>
              ) : convertedData?.raw_response ? (
                <ScrollArea className="h-[400px] border border-slate-200 p-4">
                  <pre className="text-sm text-slate-600 whitespace-pre-wrap">
                    {convertedData.raw_response}
                  </pre>
                </ScrollArea>
              ) : (
                <div className="h-[400px] flex items-center justify-center border border-dashed border-slate-300">
                  <div className="text-center">
                    <Table2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Extracted table will appear here</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Export Files Tab */}
        <TabsContent value="export">
          <div className="bg-white border border-slate-200 p-6 max-w-2xl">
            <h3 className="font-semibold text-slate-800 mb-6 flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-500" />
              Export Your Data
            </h3>
            
            {(uploadedData || convertedData?.data) ? (
              <div className="space-y-4">
                <p className="text-slate-600">
                  Export your data ({uploadedData?.row_count || convertedData?.row_count} rows) to different formats:
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    onClick={() => handleExport('excel')}
                    disabled={isExporting}
                    className="h-24 flex-col bg-green-600 hover:bg-green-500 rounded-none"
                    data-testid="export-excel-btn"
                  >
                    <FileSpreadsheet className="w-8 h-8 mb-2" />
                    Export as Excel
                  </Button>
                  
                  <Button
                    onClick={() => handleExport('pdf')}
                    disabled={isExporting}
                    className="h-24 flex-col bg-red-600 hover:bg-red-500 rounded-none"
                    data-testid="export-pdf-btn"
                  >
                    <FileType className="w-8 h-8 mb-2" />
                    Export as PDF
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <FileSpreadsheet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Upload data first to enable export</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
